import { Select as SelectPrimitive } from 'radix-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { fieldVariants } from './fieldStyles';
import { GLYPH } from './glyphs';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  value: string | undefined;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'md' | 'sm';
  id?: string;
  name?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  onBlur?: () => void;
  /** Управляемое открытие списка (например, сразу по клику на воронку фильтра колонки). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Выбор из списка (README «Поля форм → select»): триггер как поле, список — выпадающая панель. */
export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  invalid,
  size = 'md',
  id,
  name,
  className,
  onBlur,
  open,
  onOpenChange,
  ...aria
}: SelectProps) {
  const { t } = useTranslation('form');
  return (
    // Всегда управляемый: пустое значение — '' (Radix показывает placeholder), иначе после выбора
    // React предупреждает о смене uncontrolled → controlled, а сброс формы оставлял прежнюю подпись.
    <SelectPrimitive.Root
      value={value ?? ''}
      onValueChange={onValueChange}
      disabled={disabled}
      name={name}
      open={open}
      onOpenChange={onOpenChange}
    >
      <SelectPrimitive.Trigger
        id={id}
        aria-invalid={invalid || undefined}
        onBlur={onBlur}
        className={cn(
          fieldVariants({ size }),
          'flex cursor-pointer items-center gap-8 pr-10 pl-10 text-left',
          className,
        )}
        {...aria}
      >
        <span className="min-w-0 flex-1 truncate">
          <SelectPrimitive.Value placeholder={placeholder ?? t('select.placeholder')} />
        </span>
        <SelectPrimitive.Icon className="shrink-0 text-[8px] text-ink-500">
          {GLYPH.caretDown}
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={2}
          className="z-60 max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width) overflow-hidden rounded-16 border border-line-strong bg-paper-pure shadow-dropdown animate-rise-menu"
        >
          <SelectPrimitive.Viewport className="max-h-240">
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </SelectItem>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

function SelectItem({
  value,
  disabled,
  children,
}: {
  value: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <SelectPrimitive.Item
      value={value}
      disabled={disabled}
      className="flex cursor-pointer items-center border-b border-line-soft px-10 py-7 text-[13px] text-ink-900 outline-none select-none last:border-b-0 data-disabled:cursor-not-allowed data-disabled:text-ink-400 data-highlighted:bg-surface-sunken data-[state=checked]:font-semibold"
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}
